/* eslint-disable max-lines */
import TwoFactorModalManagerBase from "components/jiffy/vue-print-cart/address-section/twoFactorModalUtils";

const MODAL_SELECTOR = "#verification-code-modal";

// Delay in ms to restore modal to original parent after close
const DELAY_MS = 300;

/**
 * Manages two-factor authentication modal display and interactions
 * Extends base manager to provide address editor specific functionality
 */
class TwoFactorModalManager extends TwoFactorModalManagerBase {
  #targetContainer = null;

  #originalParent = null;

  /**
   * Creates a new TwoFactorModalManager instance
   * @param {Object} options - The context object containing required dependencies
   * @param {string} options.addressId - The ID of the address being managed
   * @param {Object} options.authHandler - Instance of TwoFactorAuthHandler
   * @param {Element} options.errorMessageElement - DOM element for error messages
   * @param {Function} options.setLoadingFn - Function to set loading state
   * @param {Element} options.targetContainer - Optional container to move modal into
   */
  constructor(options) {
    super(options);
    this.addressId = options.addressId;
    this.authHandler = options.authHandler;
    this.errorMessageElement = options.errorMessageElement;
    this.setLoadingFn = options.setLoadingFn;
    this.#targetContainer = options.targetContainer;
  }

  /**
   * Configures modal dataset attributes for state tracking
   * @param {HTMLElement} $modal - The modal element
   */
  configureModalDataset($modal) {
    // eslint-disable-next-line no-param-reassign
    $modal.dataset.preSave = "true";
    // eslint-disable-next-line no-param-reassign
    $modal.dataset.addressId = this.addressId || "new";

    $modal.classList.add("local-delivery-2fa-modal");
  }

  // eslint-disable-next-line class-methods-use-this
  get #$modal() {
    return document.querySelector(MODAL_SELECTOR);
  }

  /**
   * Moves modal to a specified parent element if not already there
   * @param {Element} targetParent - The target parent element to move modal to
   * @private
   */
  #moveModalToParent(targetParent) {
    if (!targetParent || !this.#$modal) return;

    // Only move if not already in target parent
    if (this.#$modal.parentElement !== targetParent) {
      targetParent.appendChild(this.#$modal);
    }
  }

  /**
   * Moves the modal into a target container if specified
   * @private
   */
  #moveModalToContainer() {
    if (!this.#targetContainer || !this.#$modal) return;

    // Store original parent if not already stored
    if (!this.#originalParent) {
      this.#originalParent = this.#$modal.parentElement;
    }

    this.#moveModalToParent(this.#targetContainer);
  }

  /**
   * Restores the modal to its original parent
   * @private
   */
  #restoreModalToOriginalParent() {
    this.#moveModalToParent(this.#originalParent);
  }

  /**
   * Shows the two-factor authentication modal
   * Configures modal, sets up event handlers, and displays the modal
   */
  show() {
    if (!this.#$modal) {
      if (this.errorMessageElement) {
        this.errorMessageElement.textContent = "2FA modal not found in DOM";
        this.errorMessageElement.classList.remove("hidden");
      }
      return;
    }

    // Move modal into target container if specified
    this.#moveModalToContainer();

    this.configureModalDataset(this.#$modal);

    const handleModalClose = () => {
      this.authHandler.hideModal(this.#$modal);
      // Restore modal to original position after close
      setTimeout(() => {
        this.#restoreModalToOriginalParent();
      }, DELAY_MS);
    };

    TwoFactorModalManager.setupModalCloseHandlers(this.#$modal, handleModalClose);

    try {
      TwoFactorModalManager.displayModal(this.#$modal, handleModalClose);
      this.initializeModalContent(this.#$modal);
    } catch (error) {
      if (this.errorMessageElement) {
        this.errorMessageElement.textContent = `Failed to show modal: ${error.message}`;
        this.errorMessageElement.classList.remove("hidden");
      }
    }
  }

  /**
   * Cleanup method to restore modal position
   * @public
   */
  destroy() {
    this.#restoreModalToOriginalParent();
  }
}

export default TwoFactorModalManager;
